import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Income vs Spending Chart Component
 * 
 * Displays a clear comparison of income vs spending:
 * - Income (positive green bars)
 * - Spending (negative red bars)
 * - Net Income (line showing income - spending)
 * 
 * @param {Object} props - Component props
 * @param {Array} props.incomeTrends - Array of income data by month
 * @param {Array} props.spendingTrends - Array of spending data by month
 * @param {boolean} props.isLoading - Loading state
 * @returns {JSX.Element} Income vs Spending Chart component
 */
const IncomeVsSpendingChart = ({ incomeTrends = [], spendingTrends = [], isLoading = false }) => {
  console.log('IncomeVsSpendingChart - Income:', incomeTrends);
  console.log('IncomeVsSpendingChart - Spending:', spendingTrends);

  // Generate last 6 months
  const generateMonths = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      months.push({ key: monthKey, label: monthLabel });
    }
    
    return months;
  };

  const months = generateMonths();

  // Create lookup maps for income and spending
  const incomeMap = {};
  const spendingMap = {};

  incomeTrends.forEach(item => {
    incomeMap[item.month] = item.amount || 0;
  });

  spendingTrends.forEach(item => {
    spendingMap[item.month] = item.amount || 0;
  });

  // Prepare data arrays
  const incomeData = months.map(m => incomeMap[m.key] || 0);
  const spendingData = months.map(m => -(spendingMap[m.key] || 0)); // Negative for spending
  const netIncomeData = months.map(m => (incomeMap[m.key] || 0) - (spendingMap[m.key] || 0));
  const labels = months.map(m => m.label);

  const data = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Income',
        data: incomeData,
        backgroundColor: 'rgba(34, 197, 94, 0.7)', // Green
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1,
        borderRadius: 6,
        order: 2
      },
      {
        type: 'bar',
        label: 'Spending',
        data: spendingData,
        backgroundColor: 'rgba(239, 68, 68, 0.7)', // Red
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 1,
        borderRadius: 6,
        order: 2
      },
      {
        type: 'line',
        label: 'Net Income',
        data: netIncomeData,
        borderColor: 'rgba(99, 102, 241, 1)', // Indigo
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgba(99, 102, 241, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: false,
        order: 1
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      datalabels: {
        display: false // Disable data labels on the chart
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = Math.abs(context.parsed.y);
            label += new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value);
            
            if (context.dataset.label === 'Net Income') {
              label += context.parsed.y >= 0 ? ' (Surplus)' : ' (Deficit)';
            }
            
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          },
          callback: function(value) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(Math.abs(value));
          }
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-2">
            <div className="h-2 w-48 bg-gray-200 rounded"></div>
            <div className="h-2 w-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (incomeTrends.length === 0 && spendingTrends.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-400">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="mt-2 text-sm">No income or spending data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <Chart type="bar" data={data} options={options} key={JSON.stringify(months)} />
    </div>
  );
};

export default IncomeVsSpendingChart;

